import cx from 'clsx';
import PropTypes from 'prop-types';

import Container from 'components/Container';
import { useT } from 'i18n';

import customers from '../../data/customers.json';

// `scale` in customers.json evens out the visual weight of wide and compact wordmarks.

const maskStyle = (file) => ({
  WebkitMaskImage: `url(${file})`,
  maskImage: `url(${file})`,
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
});

const LogoList = ({ duplicate }) => (
  <ul
    aria-hidden={duplicate || undefined}
    className={cx(
      'flex shrink-0 items-center gap-x-12 sm:gap-x-16 pr-12 sm:pr-16',
      'motion-reduce:w-full motion-reduce:shrink motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-y-8 motion-reduce:pr-0',
      { 'motion-reduce:hidden': duplicate },
    )}
  >
    {customers.map((customer) => (
      <li key={customer.id} className="flex shrink-0">
        <a
          href={customer.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={customer.name}
          title={customer.name}
          tabIndex={duplicate ? -1 : undefined}
          className="flex text-gray-400 hover:text-gray-700 focus-visible:text-gray-700 transition-colors"
        >
          <span
            aria-hidden="true"
            className="block h-[calc(1.75rem*var(--logo-scale))] sm:h-[calc(2rem*var(--logo-scale))] bg-current"
            style={{
              '--logo-scale': customer.scale || 1,
              aspectRatio: customer.width / customer.height,
              ...maskStyle(customer.file),
            }}
          />
        </a>
      </li>
    ))}
  </ul>
);

LogoList.propTypes = {
  duplicate: PropTypes.bool,
};

/**
 * "Trusted by" band: customer logos in a slow, looping marquee.
 * With reduced motion, the marquee stops and the logos wrap into a centered grid.
 */
const Customers = ({ className, theme = 'white', title }) => {
  const t = useT();

  return (
    <section
      className={cx(
        'py-10 sm:py-12',
        {
          'bg-white': theme === 'white',
          'bg-gray-100': theme === 'light',
          'bg-linear-to-b from-gray-50 to-gray-100': theme === 'gradient-light',
        },
        className,
      )}
    >
      <Container className="space-y-6 sm:space-y-8">
        <p className="text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
          {title ?? t('common.customers.title')}
        </p>
        <div
          className={cx(
            'overflow-hidden',
            'mask-[linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]',
            'motion-reduce:mask-none',
          )}
        >
          <div
            className={cx(
              'flex w-max animate-marquee',
              // Pause on hover. On keyboard focus only (:focus-visible), stop the marquee
              // and reset it so the focused logo is never clipped off the left edge.
              // A mouse click also focuses the link, so a plain focus-within rule would
              // move the logo away from the cursor and leave the marquee frozen.
              'hover:[animation-play-state:paused] has-[a:focus-visible]:animate-none',
              'motion-reduce:w-full motion-reduce:animate-none',
            )}
          >
            <LogoList />
            <LogoList duplicate={true} />
          </div>
        </div>
      </Container>
    </section>
  );
};

Customers.propTypes = {
  className: PropTypes.string,
  theme: PropTypes.oneOf(['white', 'light', 'gradient-light']),
  title: PropTypes.string,
};

export default Customers;
